import { useState } from "react"
import type { FormEvent, ReactNode } from "react"
import { CheckCircle2 } from "lucide-react"

import { MarketingLayout } from "@/components/marketing/MarketingLayout"
import { MinimalMarketingLayout } from "@/components/marketing/MinimalMarketingLayout"
import { Button } from "@/components/marketing/elements/button"
import { Section } from "@/components/marketing/elements/section"
import { isLaunchMode } from "@/lib/launch-config"
import { supabase } from "@/lib/supabase"
import { usePageTitle } from "@/lib/use-page-title"
import { cn } from "@/lib/utils"

function requestAccessErrorMessage(error: unknown): string {
  const message = error instanceof Error ? error.message : String(error)
  if (message.includes("email_invalid")) return "Enter a valid email address."
  if (message.includes("email_required")) return "Email is required."
  return "Something went wrong. Please try again."
}

const fieldClassName =
  "w-full min-w-0 rounded-lg bg-white px-4 py-3 text-base/6 text-mauve-950 inset-ring-1 inset-ring-black/10 placeholder:text-mauve-500 focus:inset-ring-2 focus:inset-ring-mauve-950/30 focus:outline-hidden dark:bg-white/10 dark:text-white dark:inset-ring-white/10 dark:placeholder:text-mauve-500"

function Field({
  label,
  optional,
  children,
}: {
  label: string
  optional?: boolean
  children: ReactNode
}) {
  return (
    <label className="flex flex-col gap-2">
      <span className="text-sm/6 font-medium text-mauve-950 dark:text-white">
        {label}
        {optional ? <span className="ml-1 font-normal text-mauve-500">(optional)</span> : null}
      </span>
      {children}
    </label>
  )
}

export function RequestAccessPage() {
  usePageTitle("Request access")

  const Layout = isLaunchMode() ? MinimalMarketingLayout : MarketingLayout

  const [email, setEmail] = useState("")
  const [name, setName] = useState("")
  const [company, setCompany] = useState("")
  const [role, setRole] = useState("")
  const [useCase, setUseCase] = useState("")
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [done, setDone] = useState(false)

  const onSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    setError(null)
    setLoading(true)

    const { error: rpcError } = await supabase.rpc("join_waitlist", {
      p_email: email,
      p_source: "request-access",
      p_name: name || null,
      p_company: company || null,
      p_role: role || null,
      p_use_case: useCase || null,
    })

    setLoading(false)

    if (rpcError) {
      setError(requestAccessErrorMessage(rpcError))
      return
    }

    setDone(true)
  }

  return (
    <Layout>
      <Section
        id="request-access"
        eyebrow="Private preview"
        headline="Request access"
        subheadline={<p>Tell us a bit about your team and we&apos;ll email you when access opens.</p>}
      >
        <div className="max-w-xl">
          {done ? (
            <div className="flex items-center gap-3 rounded-xl bg-verified/10 px-6 py-5 text-verified">
              <CheckCircle2 className="size-5 shrink-0" />
              <p className="text-base/6">Request received. We&apos;ll email you when access opens.</p>
            </div>
          ) : (
            <form
              onSubmit={onSubmit}
              className="flex flex-col gap-5 rounded-xl bg-mauve-950/2.5 p-6 sm:p-8 dark:bg-white/5"
            >
              <Field label="Name" optional>
                <input
                  className={fieldClassName}
                  type="text"
                  name="name"
                  autoComplete="name"
                  placeholder="Jane Doe"
                  value={name}
                  onChange={(event) => setName(event.target.value)}
                  disabled={loading}
                />
              </Field>
              <Field label="Work email">
                <input
                  className={fieldClassName}
                  type="email"
                  name="email"
                  autoComplete="email"
                  required
                  placeholder="jane@company.com"
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                  disabled={loading}
                />
              </Field>
              <Field label="Company" optional>
                <input
                  className={fieldClassName}
                  type="text"
                  name="company"
                  autoComplete="organization"
                  placeholder="Acme Inc."
                  value={company}
                  onChange={(event) => setCompany(event.target.value)}
                  disabled={loading}
                />
              </Field>
              <Field label="Role" optional>
                <input
                  className={fieldClassName}
                  type="text"
                  name="role"
                  autoComplete="organization-title"
                  placeholder="Engineering, Product, QA…"
                  value={role}
                  onChange={(event) => setRole(event.target.value)}
                  disabled={loading}
                />
              </Field>
              <Field label="What would you use ArtlessDev for?" optional>
                <textarea
                  className={cn(fieldClassName, "min-h-28 resize-y")}
                  name="use_case"
                  placeholder="Tell us about your team and what you're hoping to track."
                  value={useCase}
                  onChange={(event) => setUseCase(event.target.value)}
                  disabled={loading}
                />
              </Field>
              {error ? <p className="text-sm text-destructive">{error}</p> : null}
              <Button type="submit" disabled={loading} size="lg" className="self-start">
                {loading ? "Requesting…" : "Request access"}
              </Button>
            </form>
          )}
        </div>
      </Section>
    </Layout>
  )
}
